import { PersistConfig, Transform, createTransform } from 'redux-persist'
import storageSession from 'redux-persist/lib/storage/session'
import { DEBUG_MODE } from '../env'
import { ConsumerAppState } from './store'
import { apiService } from '@afterpaytouch/portal-api/api'
import { apiService as topazService } from '@afterpaytouch/topaz-api/api'
import { apiService as shopService } from '@afterpaytouch/shop-api/api'
import { paymentService } from './payments'
import { QueryStatus } from '@reduxjs/toolkit/query'
import type { RegistrationState } from './registration/reducer'

type RTKQueryState = Record<string, any> & {
  queries?: Record<string, { status?: QueryStatus }>
}

/**
 * Transform to clean up RTK Query state before persistence
 * Based on checkout app pattern - removes pending queries and sensitive cache data
 */
const cleanupRTKQueryState: Transform<any, any> = createTransform(
  (inboundState: RTKQueryState, key): RTKQueryState => {
    const apiPaths: string[] = [apiService.service.reducerPath, topazService.service.reducerPath, shopService.service.reducerPath, paymentService.reducerPath]

    if (typeof key !== 'string' || !apiPaths.includes(key)) {
      return inboundState
    }

    const { queries = {} } = inboundState

    // Remove pending queries as they won't work after rehydration
    const filteredQueries = Object.fromEntries(Object.entries(queries).filter(([_, entry]) => entry?.status !== QueryStatus.pending))

    return {
      ...inboundState,
      queries: filteredQueries,
      subscriptions: {},
      mutations: {},
    }
  },
  (outboundState) => outboundState,
  {
    whitelist: [apiService.service.reducerPath, topazService.service.reducerPath, shopService.service.reducerPath, paymentService.reducerPath],
  }
)

const clearTerminalErrorAcknowledgement: Transform<RegistrationState, RegistrationState> = createTransform(
  (inboundState: RegistrationState): RegistrationState => ({
    ...inboundState,
    terminalErrorAcknowledged: false,
  }),
  (outboundState: RegistrationState): RegistrationState => outboundState,
  { whitelist: ['registration'] }
)

/**
 * Redux Persist configuration for portal application
 *
 * Uses session storage for security (cleared on tab close)
 * Excludes sensitive data and API caches from persistence
 * Includes timeout and error handling for production stability
 */
export const persistConfig: PersistConfig<ConsumerAppState> = {
  key: 'portal-root',
  version: 1.0,
  storage: storageSession,
  timeout: 2000,
  blacklist: ['cardScan', 'dispute'],
  transforms: [cleanupRTKQueryState, clearTerminalErrorAcknowledgement],
  debug: DEBUG_MODE,
}
